import { IOGame } from "./game";
import type { IOGameState } from "./game";

// ─── Sandbox debug overlay ────────────────────────────────────────────────────
// Only active when running in the prototype (index.html has #io-game-root).
// Not included in the live Webflow embed path.

function initDebugPanel(getContainer: () => HTMLElement): void {
  const stateEl = document.getElementById("dbg-state");
  const deviceEl = document.getElementById("dbg-device");
  const mobileToggle = document.getElementById("dbg-mobile-toggle") as HTMLButtonElement | null;

  // Sandbox modes: desktop (default) | mobile-loop (forceMobile) | touch-interactive
  type SandboxMode = "desktop" | "mobile-loop" | "touch";
  let sandboxMode: SandboxMode = "desktop";
  let currentGame: IOGame | null = null;

  const touchToggle = document.getElementById("dbg-touch-toggle") as HTMLButtonElement | null;

  function spawnGame() {
    currentGame?.destroy();
    const opts = sandboxMode === "mobile-loop"
      ? { forceMobile: true }
      : sandboxMode === "touch"
      ? { touchInteractive: true }
      : {};

    currentGame = new IOGame(getContainer(), {
      ...opts,
      onStateChange: (state: IOGameState) => {
        if (stateEl) stateEl.textContent = state.toUpperCase().replace("-", " ");
        if (deviceEl) deviceEl.textContent =
          sandboxMode === "mobile-loop" ? "COARSE (forced)" :
          sandboxMode === "touch" ? "TOUCH (forced)" :
          window.matchMedia("(pointer: fine)").matches ? "FINE" : "COARSE";
      },
    });
    if (deviceEl) deviceEl.textContent =
      sandboxMode === "mobile-loop" ? "COARSE (forced)" :
      sandboxMode === "touch" ? "TOUCH (forced)" :
      window.matchMedia("(pointer: fine)").matches ? "FINE" : "COARSE";

    // Highlight active mode button
    mobileToggle && (mobileToggle.style.background = sandboxMode === "mobile-loop" ? "#2a4a2a" : "");
    touchToggle && (touchToggle.style.background = sandboxMode === "touch" ? "#2a2a4a" : "");
  }

  mobileToggle?.addEventListener("click", () => {
    sandboxMode = sandboxMode === "mobile-loop" ? "desktop" : "mobile-loop";
    mobileToggle.textContent = sandboxMode === "mobile-loop" ? "← Desktop" : "Simulate Mobile Loop";
    if (sandboxMode === "mobile-loop" && touchToggle) touchToggle.textContent = "Simulate Touch";
    spawnGame();
  });

  touchToggle?.addEventListener("click", () => {
    sandboxMode = sandboxMode === "touch" ? "desktop" : "touch";
    touchToggle.textContent = sandboxMode === "touch" ? "← Desktop" : "Simulate Touch";
    if (sandboxMode === "touch" && mobileToggle) mobileToggle.textContent = "Simulate Mobile Loop";
    spawnGame();
  });

  spawnGame();
}

// ─── Entry point ──────────────────────────────────────────────────────────────

function init(): void {
  const isPrototype = !!document.getElementById("io-game-root");

  if (isPrototype) {
    // Prototype sandbox — single container, debug panel wired up
    const container = document.getElementById("io-game-root")!;
    initDebugPanel(() => container);
    return;
  }

  // Live Webflow embed — initialize whichever containers are present on this page.
  // Webflow hides the non-matching breakpoint element with display:none, so
  // IntersectionObserver on the hidden one will never fire — safe to init both.

  const desktop = document.getElementById("io-container");
  if (desktop) {
    // Desktop card: natural pointer detection (interactive hover on fine-pointer devices)
    new IOGame(desktop);
  }

  const mobile = document.getElementById("io-container-mobile");
  if (mobile) {
    // Mobile card: touch-interactive — finger drives the avatar, same mechanic as desktop hover.
    // Switch to { forceMobile: true } to use the scripted column loop instead.
    new IOGame(mobile, { touchInteractive: true });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
